import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from './api';
import { LeagueBar } from './components/LeagueBar';
import { ComparePanel } from './components/ComparePanel';
import { StatisticsPanel } from './components/StatisticsPanel';
import { ResultsPanel } from './components/ResultsPanel';
import { ManualStatsPanel } from './components/ManualStatsPanel';
import { HistoryPanel } from './components/HistoryPanel';
import type { League } from './types';

type Tab = 'compare' | 'manual' | 'history' | 'statistics' | 'results';

const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'compare', label: 'Compare & predict' },
  { id: 'manual', label: 'Manual stats' },
  { id: 'history', label: 'History' },
  { id: 'statistics', label: 'Statistics' },
  { id: 'results', label: 'Player results' },
];

const isTab = (value: string): value is Tab => TABS.some((t) => t.id === value);

/** Tabs live in the URL hash so a view can be linked to and survives a reload. */
const tabFromHash = (): Tab => {
  const hash = window.location.hash.replace(/^#/, '');
  return isTab(hash) ? hash : 'compare';
};

export default function App() {
  const [leagues, setLeagues] = useState<League[]>([]);
  const [leagueId, setLeagueId] = useState<number | null>(null);
  const [tab, setTabState] = useState<Tab>(tabFromHash);
  const [error, setError] = useState<string | null>(null);

  const setTab = (next: Tab) => {
    setTabState(next);
    window.location.hash = next;
  };

  useEffect(() => {
    const onHashChange = () => setTabState(tabFromHash());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const reload = useCallback(
    async (select?: number) => {
      try {
        const list = await api.leagues();
        setLeagues(list);
        setError(null);
        setLeagueId((current) => select ?? current ?? list[0]?.id ?? null);
      } catch (err) {
        setError(
          err instanceof ApiError
            ? `Could not reach the API: ${err.message}`
            : 'Could not reach the API. Is the backend running?',
        );
      }
    },
    [],
  );

  useEffect(() => {
    void reload();
  }, [reload]);

  return (
    <div className="app">
      <div className="topbar">
        <div className="brand">
          <h1>Dart Performance Analyser</h1>
          <small>3K-Dart-Software league analysis</small>
        </div>
        <div className="tabs">
          {TABS.map((t) => (
            <button key={t.id} aria-selected={tab === t.id} onClick={() => setTab(t.id)}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="notice error">{error}</div>}

      <LeagueBar
        leagues={leagues}
        selectedId={leagueId}
        onSelect={setLeagueId}
        onIngested={(id) => void reload(id)}
      />

      {leagueId === null ? (
        <section className="card">
          <div className="empty-state">
            No league imported yet. Paste a 3K-Dart-Software league URL above to get started.
          </div>
        </section>
      ) : tab === 'compare' ? (
        <ComparePanel key={leagueId} leagueId={leagueId} />
      ) : tab === 'manual' ? (
        <ManualStatsPanel key={leagueId} leagueId={leagueId} />
      ) : tab === 'history' ? (
        <HistoryPanel key={leagueId} leagueId={leagueId} />
      ) : tab === 'statistics' ? (
        <StatisticsPanel key={leagueId} leagueId={leagueId} />
      ) : (
        <ResultsPanel key={leagueId} leagueId={leagueId} />
      )}
    </div>
  );
}
